import { useState } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: Convex generated API lives outside src/; no @convex/ path alias in tsconfig
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { INDUSTRIES } from '@/pages/businessSettingsConstants';
import type { ICreateOrgDialogProps, ICreateOrgFormData } from '@/@types/organisation';

const INITIAL_FORM: ICreateOrgFormData = {
  name: '',
  industry: '',
  customIndustry: '',
};

export function CreateOrgDialog({ open, onOpenChange }: ICreateOrgDialogProps) {
  const createOrganisation = useMutation(api.organisations.mutations.createOrganisation);

  const [formData, setFormData] = useState<ICreateOrgFormData>(INITIAL_FORM);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleOpenChange = (value: boolean) => {
    if (!value) {
      setFormData(INITIAL_FORM);
      setError(null);
    }
    onOpenChange(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Organisation name is required');
      return;
    }
    if (!formData.industry) {
      setError('Industry is required');
      return;
    }
    if (formData.industry === 'other' && !formData.customIndustry.trim()) {
      setError('Please enter your industry');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const industryValue =
        formData.industry === 'other' ? formData.customIndustry.trim() : formData.industry;

      await createOrganisation({
        name: formData.name.trim(),
        industry: industryValue,
      });

      handleOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create organisation');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Organisation</DialogTitle>
          <DialogDescription>
            Create a new organisation to collaborate with your team.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="org-name">Organisation Name</Label>
            <Input
              id="org-name"
              placeholder="Enter organisation name"
              value={formData.name}
              onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="org-industry">Industry</Label>
            <Select
              value={formData.industry}
              onValueChange={value =>
                setFormData(prev => ({ ...prev, industry: value, customIndustry: '' }))
              }
              disabled={isSubmitting}
            >
              <SelectTrigger id="org-industry">
                <SelectValue placeholder="Select your industry" />
              </SelectTrigger>
              <SelectContent>
                {INDUSTRIES.map(ind => (
                  <SelectItem key={ind.value} value={ind.value}>
                    {ind.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {formData.industry === 'other' && (
            <div className="space-y-2">
              <Label htmlFor="org-custom-industry">Your Industry</Label>
              <Input
                id="org-custom-industry"
                placeholder="Describe your industry"
                value={formData.customIndustry}
                onChange={e => setFormData(prev => ({ ...prev, customIndustry: e.target.value }))}
                disabled={isSubmitting}
              />
            </div>
          )}

          {error && (
            <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}
              Create Organisation
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
